import React, { memo, useState, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { createUseStyles, Text, Advice, Input, Modal } from '@Sage/hoi-poi-ui';

import { useWeb5Context } from 'lib/Web5Context';
import { breakLines } from 'utils/string';

import styles from './styles';
const useStyles = createUseStyles(styles, 'ModalConfirm');

const ModalConfirm = ({
    onConfirm,
    onCancel,
    isOpen,
    title,
    description,
    advice,
    writeXToConfirm,
    passPhrase,
    confirmText,
    isConfirmLoading,
}) => {
    const { t } = useWeb5Context();
    const [value, setValue] = useState('');
    const classes = useStyles();

    const onValueChange = useCallback((value) => setValue(value || ''), [setValue]);

    const handleOnConfirm = useCallback(() => {
        if (value?.toLowerCase() !== passPhrase.toLowerCase()) return;
        setValue('');
        onConfirm && onConfirm();
    }, [value, passPhrase, onConfirm]);

    const handleOnCancel = useCallback(() => {
        onCancel && onCancel();
        setValue();
    }, [onCancel]);

    const overrides = useMemo(
        () => ({
            confirmButton: {
                className:
                    value?.toLowerCase() !== passPhrase.toLowerCase()
                        ? ['fm-button__disabled']
                        : [],
                isDisabled: !(value?.toLowerCase() === passPhrase.toLowerCase()),
            },
        }),
        [value, passPhrase],
    );

    const modalProps = useMemo(
        () => ({
            isOpen,
            onRequestClose: handleOnCancel,
            onCancel: handleOnCancel,
            onConfirm: handleOnConfirm,
            title,
            cancelText: t('cfm_action_cancel'),
            confirmText,
            className: classes.root,
            overlayClassName: classes.overlay,
            isConfirmLoading,
        }),

        [
            isOpen,
            handleOnCancel,
            handleOnConfirm,
            title,
            t,
            confirmText,
            classes.root,
            classes.overlay,
            isConfirmLoading,
        ],
    );

    return (
        <Modal {...modalProps} overrides={overrides}>
            <div className={classes.confirmContent}>
                {description && (
                    <Text className={classes.confirmText}>{breakLines(description)}</Text>
                )}
                {advice && (
                    <Advice
                        showCollapse={false}
                        className={classes.confirmAdvice}
                        type="warning"
                        showIcon
                    >
                        {breakLines(advice)}
                    </Advice>
                )}
                <Text>{t(writeXToConfirm, [passPhrase.toUpperCase()])}</Text>
            </div>
            <div className={classes.confirmInput}>
                <Input
                    placeholder={t('placeholder_text_field')}
                    value={value}
                    onChange={onValueChange}
                />
            </div>
        </Modal>
    );
};

ModalConfirm.propTypes = {
    onConfirm: PropTypes.func.isRequired,
    onClose: PropTypes.func,
    onCancel: PropTypes.func.isRequired,
    isOpen: PropTypes.bool.isRequired,
};

export default memo(ModalConfirm);
